// SubtitleEditorPage.tsx

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Cross } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import { Button, TEOLoader } from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import {
  SubtitleEditor,
  parseSrt,
  parseVtt,
  subtitlesToVtt,
} from './Froala/widgets/SubtitleEditor';
import { SubtitleStripTimeline } from './Froala/widgets/SubtitleStripTimeline';

type SubtitleCue = {
  id?: string;
  start_ms: number;
  end_ms: number;
  text: string;
};

type TimelineUpdate = {
  _origIndex: number;
  patch: Partial<SubtitleCue>;
};

type SubtitleEditorPageProps = {
  isOpen: boolean;
  videoUrl?: string;
  lang?: string;
  iframeEl?: HTMLIFrameElement | null;
  editor?: any;
  onClose: () => void;
};

function addReloadTokenToUrl(src: string, token = Date.now()) {
  if (!src) return src;

  try {
    const url = new URL(src, window.location.href);
    url.searchParams.set('subtitleReload', String(token));
    return url.toString();
  } catch {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}subtitleReload=${token}`;
  }
}

function parseSubtitleContent(content: string, format?: string): SubtitleCue[] {
  if (!content) return [];

  if (format === 'srt') {
    return parseSrt(content);
  }

  return parseVtt(content);
}

const EMPTY_VTT_BODY = 'WEBVTT\n\n';

function SubtitleEditorPage({
  isOpen,
  videoUrl,
  lang,
  iframeEl,
  onClose,
}: SubtitleEditorPageProps) {
  const { t } = useTranslation();

  const subtitleListScrollRef = useRef<HTMLDivElement | null>(null);
  const ignoredPollingVttRef = useRef('');

  const [subtitleCues, setSubtitleCues] = useState<SubtitleCue[]>([]);
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [trackFound, setTrackFound] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [trackNotice, setTrackNotice] = useState('');
  const [shouldPollTrack, setShouldPollTrack] = useState(false);
  const [subtitleEditorResetKey, setSubtitleEditorResetKey] = useState(0);
  const [activeSubtitleIndex, setActiveSubtitleIndex] = useState<number | null>(
    null
  );
  const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
  const SubtitleEditorAny = SubtitleEditor as any;
  const [videoReloadToken, setVideoReloadToken] = useState(0);

  const previewVideoUrl = useMemo(() => {
    if (!videoUrl || !videoReloadToken) return videoUrl;

    return addReloadTokenToUrl(videoUrl, videoReloadToken);
  }, [videoUrl, videoReloadToken]);

  const effectiveLang = lang || 'en';
  const isWaitingForRegeneratedTrack = shouldPollTrack || Boolean(trackNotice);

  const reloadVideoIframes = useCallback(
    (token = Date.now()) => {
      setVideoReloadToken(token);

      if (iframeEl?.src) {
        iframeEl.src = addReloadTokenToUrl(iframeEl.src, token);
      }
    },
    [iframeEl]
  );

  const durationSec = useMemo(() => {
    const maxEndMs = subtitleCues.reduce(
      (max, cue) => Math.max(max, cue.end_ms || 0),
      0
    );

    return Math.max(1, Math.ceil(maxEndMs / 1000));
  }, [subtitleCues]);

  const vttPreview = useMemo(() => {
    if (!subtitleCues.length) return '';
    return subtitlesToVtt(subtitleCues);
  }, [subtitleCues]);

  const scrollSelectedSubtitleIntoView = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const viewport = subtitleListScrollRef.current;
      if (!viewport || activeSubtitleIndex == null) return;

      requestAnimationFrame(() => {
        const nextViewport = subtitleListScrollRef.current;
        if (!nextViewport) return;

        /*
          SubtitleEditor does not currently expose a data attribute per cue.
          The selected cue card receives border-secondary-04, so we use that
          inside this scroll container only.
        */
        const selectedEl = nextViewport.querySelector(
          '.border-secondary-04'
        ) as HTMLElement | null;

        if (!selectedEl) return;

        const viewportRect = nextViewport.getBoundingClientRect();
        const selectedRect = selectedEl.getBoundingClientRect();
        const padding = 32;

        const isAbove = selectedRect.top < viewportRect.top + padding;
        const isBelow = selectedRect.bottom > viewportRect.bottom - padding;

        if (!isAbove && !isBelow) return;

        const targetTop =
          selectedEl.offsetTop -
          nextViewport.clientHeight / 2 +
          selectedEl.offsetHeight / 2;

        nextViewport.scrollTo({
          top: Math.max(0, targetTop),
          behavior,
        });
      });
    },
    [activeSubtitleIndex]
  );

  const handleSeek = useCallback((timeSec: number) => {
    if (!Number.isFinite(timeSec)) return;

    setPreviewCurrentTime(Math.max(0, timeSec));
  }, []);

  const handleSetActiveSubtitleIndex = useCallback(
    (index: number | null) => {
      setActiveSubtitleIndex(index);

      if (index == null) return;

      const cue = subtitleCues[index - 1];
      if (!cue) return;

      const startSec = Math.max(0, (cue.start_ms || 0) / 1000);
      const endSec = Math.max(startSec, (cue.end_ms || 0) / 1000);

      setPreviewCurrentTime((prev) => {
        if (Number.isFinite(prev) && prev >= startSec && prev <= endSec) {
          return prev;
        }

        return startSec + 0.05;
      });
    },
    [subtitleCues]
  );

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setSubtitleCues([]);
    setTrackError('');
    setTrackNotice('');
    setTrackFound(false);
    setDirty(false);
    setActiveSubtitleIndex(null);
    setPreviewCurrentTime(0);
    setVideoReloadToken(0);
    setShouldPollTrack(false);
    ignoredPollingVttRef.current = '';
    setSubtitleEditorResetKey((key) => key + 1);
  }, [isOpen, videoUrl, effectiveLang]);

  const loadVimeoTextTrack = useCallback(
    async ({
      showLoader = true,
      silent = false,
      ignoreEmptyTrack = false,
      ignoreContent = '',
    }: {
      showLoader?: boolean;
      silent?: boolean;
      ignoreEmptyTrack?: boolean;
      ignoreContent?: string;
    } = {}) => {
      if (!videoUrl) return false;

      try {
        if (showLoader) {
          setIsLoadingTrack(true);
        }

        if (!silent) {
          setTrackError('');
        }

        const res = await axios.post(
          `${config.backend}/external/v1/instructai/vimeo/texttrack`,
          {
            videoUrl,
            lang: effectiveLang,
            kind: 'subtitles',
          }
        );

        const data = res?.data;
        const hasTrackWithContent = Boolean(data?.found && data?.content);

        setTrackFound(Boolean(data?.found));

        if (hasTrackWithContent) {
          const parsedCues = parseSubtitleContent(
            data.content,
            data.format || 'vtt'
          );
          const normalizedVtt = subtitlesToVtt(parsedCues).trim();

          if (ignoreContent && normalizedVtt === ignoreContent.trim()) {
            return false;
          }

          if (ignoreEmptyTrack && !parsedCues.length) {
            setTrackFound(false);
            return false;
          }

          setSubtitleCues(parsedCues);
          setActiveSubtitleIndex(parsedCues.length ? 1 : null);
          setPreviewCurrentTime(
            parsedCues.length ? (parsedCues[0].start_ms || 0) / 1000 + 0.05 : 0
          );
          setDirty(false);
          setTrackNotice('');
          setShouldPollTrack(false);
          ignoredPollingVttRef.current = '';
          setSubtitleEditorResetKey((key) => key + 1);

          if (silent) {
            reloadVideoIframes();
          }

          return true;
        }

        if (!silent) {
          setSubtitleCues([]);
          setActiveSubtitleIndex(null);
          setPreviewCurrentTime(0);
          setDirty(false);
        }

        return false;
      } catch (err: any) {
        console.error(
          '[SubtitleEditorPage] Failed to load Vimeo text track',
          err
        );

        if (!silent) {
          setTrackError(
            err?.response?.data?.error?.message ||
              err?.message ||
              'Failed to load subtitles'
          );
        }

        return false;
      } finally {
        if (showLoader) {
          setIsLoadingTrack(false);
        }
      }
    },
    [effectiveLang, reloadVideoIframes, videoUrl]
  );

  useEffect(() => {
    if (!isOpen || !videoUrl) return;

    loadVimeoTextTrack();
  }, [isOpen, videoUrl, effectiveLang, loadVimeoTextTrack]);

  useEffect(() => {
    if (!isOpen || !videoUrl || !shouldPollTrack) return;

    const intervalId = window.setInterval(() => {
      loadVimeoTextTrack({
        showLoader: false,
        silent: true,
        ignoreEmptyTrack: true,
        ignoreContent: ignoredPollingVttRef.current,
      });
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen, videoUrl, shouldPollTrack, loadVimeoTextTrack]);

  useEffect(() => {
    setActiveSubtitleIndex((prev) => {
      if (prev == null) return prev;
      if (!subtitleCues.length) return null;
      if (prev < 1) return 1;
      if (prev > subtitleCues.length) return subtitleCues.length;
      return prev;
    });
  }, [subtitleCues.length]);

  useEffect(() => {
    if (activeSubtitleIndex == null) return;

    const cue = subtitleCues[activeSubtitleIndex - 1];
    if (!cue) return;

    const startSec = Math.max(0, (cue.start_ms || 0) / 1000);
    const endSec = Math.max(startSec, (cue.end_ms || 0) / 1000);

    setPreviewCurrentTime((prev) => {
      if (Number.isFinite(prev) && prev >= startSec && prev <= endSec) {
        return prev;
      }

      return startSec + 0.05;
    });
  }, [activeSubtitleIndex, subtitleCues]);

  useEffect(() => {
    if (isLoadingTrack || trackError || activeSubtitleIndex == null) return;

    scrollSelectedSubtitleIntoView();
  }, [
    activeSubtitleIndex,
    isLoadingTrack,
    trackError,
    subtitleCues.length,
    scrollSelectedSubtitleIntoView,
  ]);

  const handleSubtitleChange = useCallback((next: SubtitleCue[]) => {
    setSubtitleCues(next);
    setDirty(true);

    setActiveSubtitleIndex((prev) => {
      if (prev == null) return next.length ? 1 : null;
      if (!next.length) return null;
      if (prev > next.length) return next.length;
      return prev;
    });
  }, []);

  const handleUpdateSubtitleTiming = useCallback(
    (index: number, startMs: number, endMs: number) => {
      setSubtitleCues((prev) => {
        if (!prev[index]) return prev;

        const next = prev.slice();

        next[index] = {
          ...next[index],
          start_ms: startMs,
          end_ms: endMs,
        };

        return next;
      });

      setDirty(true);
    },
    []
  );

  const handleUpdateManySubtitleTimings = useCallback(
    (updates: TimelineUpdate[]) => {
      setSubtitleCues((prev) => {
        const next = prev.slice();

        updates.forEach(({ _origIndex, patch }) => {
          if (!next[_origIndex]) return;

          next[_origIndex] = {
            ...next[_origIndex],
            ...patch,
          };
        });

        return next;
      });

      setDirty(true);
    },
    []
  );

  const enterAiSubtitlePendingState = useCallback(
    ({
      notice,
      ignoreContent = '',
    }: {
      notice: string;
      ignoreContent?: string;
    }) => {
      ignoredPollingVttRef.current = ignoreContent.trim();

      setTrackError('');
      setTrackNotice(notice);
      setSubtitleCues([]);
      setTrackFound(false);
      setDirty(false);
      setActiveSubtitleIndex(null);
      setPreviewCurrentTime(0);
      setShouldPollTrack(true);
      setSubtitleEditorResetKey((key) => key + 1);

      subtitleListScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      reloadVideoIframes(Date.now());
    },
    [reloadVideoIframes]
  );

  const handleGenerateWithAi = async () => {
    if (!videoUrl) return;

    setIsRegenerating(true);

    enterAiSubtitlePendingState({
      notice: t('pages.edit_subtitle.generation_started_return_later', {
        defaultValue:
          'Subtitle generation has started. This page will automatically reload the subtitles when they are ready, or you can return later.',
      }),
      ignoreContent: EMPTY_VTT_BODY.trim(),
    });

    try {
      await axios.post(`${config.backend}/external/v1/instructai/start`, {
        videoUrl,
        lang: effectiveLang,
        targetLang: effectiveLang,
        task: 'translate_video',
        replaceExisting: true,
      });

      reloadVideoIframes(Date.now());
    } catch (err: any) {
      console.error(
        '[SubtitleEditorPage] Failed to generate subtitles with AI',
        err
      );

      setShouldPollTrack(false);
      setTrackNotice('');

      setTrackError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'Failed to generate subtitles with AI'
      );
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRegenerateWithAi = async () => {
    if (!videoUrl) return;

    setIsRegenerating(true);

    // Remember the current track so polling does not treat the old Vimeo track
    // as the regenerated result if Vimeo/cache returns it briefly.
    enterAiSubtitlePendingState({
      notice: t('pages.edit_subtitle.regeneration_started_return_later', {
        defaultValue:
          'Subtitle regeneration has started. The old subtitle track has been removed. This page will automatically reload the new subtitles when they are ready, or you can return later.',
      }),
      ignoreContent: subtitlesToVtt(subtitleCues).trim(),
    });

    try {
      // Remove the current Vimeo text track instead of uploading an empty VTT.
      await axios.post(
        `${config.backend}/external/v1/instructai/vimeo/texttrack/delete`,
        {
          videoUrl,
          lang: effectiveLang,
          kind: 'subtitles',
        }
      );

      // Start the actual regeneration job.
      await axios.post(`${config.backend}/external/v1/instructai/start`, {
        videoUrl,
        lang: effectiveLang,
        targetLang: effectiveLang,
        task: 'translate_video',
        regenerate: true,
        replaceExisting: true,
      });

      reloadVideoIframes(Date.now());
    } catch (err: any) {
      console.error(
        '[SubtitleEditorPage] Failed to regenerate subtitles with AI',
        err
      );

      setShouldPollTrack(false);
      setTrackNotice('');

      setTrackError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'Failed to regenerate subtitles with AI'
      );
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setTrackError('');
      setTrackNotice('');

      const vttBody = subtitlesToVtt(subtitleCues);

      await axios.post(
        `${config.backend}/external/v1/instructai/vimeo/texttrack/save`,
        {
          videoUrl,
          lang: effectiveLang,
          kind: 'subtitles',
          vttBody,
        }
      );

      setDirty(false);
      setTrackFound(true);

      reloadVideoIframes();
    } catch (err: any) {
      console.error(
        '[SubtitleEditorPage] Failed to save Vimeo text track',
        err
      );

      setTrackError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'Failed to save subtitles'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[999999] bg-grey-01"
    >
      <div className="flex h-full flex-col">
        <div className="flex min-h-[72px] items-center border-b border-grey-02 bg-white px-4 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="-ml-2 mr-4 rounded p-2 hover:bg-grey-01"
            aria-label={t('button.close', { defaultValue: 'Close' })}
          >
            <Cross className="w-5" />
          </button>

          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold sm:text-xl">
              {t('pages.edit_subtitle.title')}
            </h2>

            {videoUrl && (
              <div className="truncate text-sm text-grey-06">{videoUrl}</div>
            )}
          </div>

          <div className="ml-auto hidden items-center gap-2 sm:flex">
            {dirty && (
              <span className="text-sm text-grey-06">
                {t('common.unsaved_changes', {
                  defaultValue: 'Unsaved changes',
                })}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto xl:overflow-hidden">
          <div className="mx-auto flex min-h-full w-full max-w-[1680px] flex-col px-4 py-5 sm:px-6 lg:py-8 xl:h-full xl:min-h-0 xl:px-10">
            <div className="grid flex-1 gap-6 xl:min-h-0 xl:grid-cols-[minmax(0,1.15fr)_minmax(560px,1fr)] xl:items-center">
              <section className="flex min-h-0 flex-col xl:justify-center">
                <div className="w-full">
                  {videoUrl ? (
                    <div className="aspect-video w-full overflow-hidden rounded-lg bg-black shadow-sm">
                      <iframe
                        key={previewVideoUrl}
                        src={previewVideoUrl}
                        title={t('pages.edit_subtitle.video_preview')}
                        className="h-full w-full"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-grey-02 bg-white text-sm text-grey-06">
                      {t('pages.edit_subtitle.no_video_selected', {
                        defaultValue: 'No video selected.',
                      })}
                    </div>
                  )}

                  <div className="mt-5 rounded-lg border border-grey-02 bg-white px-4 py-4 shadow-sm">
                    {subtitleCues.length > 0 &&
                    !isWaitingForRegeneratedTrack ? (
                      <SubtitleStripTimeline
                        key={`subtitle-strip-${subtitleEditorResetKey}`}
                        durationSec={durationSec}
                        subtitles={subtitleCues}
                        currentTime={previewCurrentTime}
                        activeSubtitleIndex={activeSubtitleIndex}
                        setActiveSubtitleIndex={handleSetActiveSubtitleIndex}
                        onSeek={handleSeek}
                        onUpdateSubtitle={handleUpdateSubtitleTiming}
                        onUpdateMany={handleUpdateManySubtitleTimings}
                      />
                    ) : (
                      <div className="rounded-lg bg-grey-01 px-3 py-4 text-center text-sm text-grey-06">
                        {isWaitingForRegeneratedTrack
                          ? trackNotice ||
                            t(
                              'pages.edit_subtitle.generation_started_return_later',
                              {
                                defaultValue:
                                  'Subtitle generation has started. This page will automatically reload the subtitles when they are ready, or you can return later.',
                              }
                            )
                          : t('pages.edit_subtitle.no_subtitle_timeline', {
                              defaultValue:
                                'No subtitle timeline available yet.',
                            })}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section className="min-h-[420px] rounded-lg border border-grey-02 bg-white shadow-sm xl:flex xl:h-full xl:min-h-0 xl:min-w-[560px] xl:flex-col">
                {' '}
                <div className="border-b border-grey-02 px-4 py-4 sm:px-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold">
                        {t('pages.edit_subtitle.subtitle_list', {
                          defaultValue: 'Subtitles',
                        })}
                      </h3>

                      <div className="text-sm text-grey-06">
                        {effectiveLang.toUpperCase()}
                      </div>
                    </div>

                    {trackFound && (
                      <span className="rounded-full bg-grey-01 px-3 py-1 text-xs text-grey-07">
                        {subtitleCues.length}{' '}
                        {t('pages.edit_subtitle.cues', {
                          defaultValue: 'cues',
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <div
                  ref={subtitleListScrollRef}
                  className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5"
                >
                  {isLoadingTrack && (
                    <div className="flex min-h-[260px] w-full">
                      <div className="m-auto">
                        <TEOLoader
                          loaderSrc="/images/TEOloader.gif"
                          className="w-[90px]"
                        />
                      </div>
                    </div>
                  )}

                  {!isLoadingTrack && trackError && (
                    <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                      {trackError}
                    </div>
                  )}

                  {!isLoadingTrack && !trackError && trackNotice && (
                    <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                      {trackNotice}
                    </div>
                  )}

                  {!isLoadingTrack &&
                    !trackError &&
                    !trackNotice &&
                    !trackFound && (
                      <div className="mb-4 rounded-lg bg-grey-01 px-4 py-3 text-sm text-grey-08">
                        {t('pages.edit_subtitle.no_existing_track', {
                          defaultValue:
                            'No existing subtitle track was found for this language.',
                        })}
                      </div>
                    )}

                  {!isLoadingTrack &&
                    !trackError &&
                    !isWaitingForRegeneratedTrack && (
                      <SubtitleEditorAny
                        key={`subtitle-editor-${subtitleEditorResetKey}`}
                        value={subtitleCues}
                        onChange={handleSubtitleChange}
                        durationSec={durationSec}
                        currentTime={previewCurrentTime}
                        onSeek={handleSeek}
                        activeSubtitleIndex={activeSubtitleIndex}
                        setActiveSubtitleIndex={handleSetActiveSubtitleIndex}
                        trackLanguage={effectiveLang}
                        emptyLabel={t('pages.edit_subtitle.no_subtitles', {
                          defaultValue: 'No subtitles loaded yet.',
                        })}
                        showTimeline={false}
                        hideTimelineToggle
                        hideEmptyGenerateAction
                        hideMeta
                      />
                    )}

                  {/* Temporary debug aid while wiring save. Remove later. */}
                  {false && <pre>{vttPreview}</pre>}
                </div>
              </section>
            </div>
          </div>
        </div>

        <div className="border-t border-grey-02 bg-white px-4 py-4 sm:px-6">
          <div className="mx-auto flex w-full max-w-[1680px] flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-6 py-2 text-sm font-semibold text-grey-08 hover:bg-grey-01"
            >
              {t('button.cancel', { defaultValue: 'Cancel' })}
            </button>

            {subtitleCues.length === 0 && !isWaitingForRegeneratedTrack && (
              <Button
                variant="outline"
                onClick={handleGenerateWithAi}
                disabled={
                  !videoUrl || isLoadingTrack || isSaving || isRegenerating
                }
              >
                {isRegenerating
                  ? t('pages.edit_subtitle.generating_with_ai', {
                      defaultValue: 'Generating...',
                    })
                  : t('pages.edit_subtitle.generate_with_ai', {
                      defaultValue: 'Generate with AI',
                    })}
              </Button>
            )}

            {subtitleCues.length > 0 && !isWaitingForRegeneratedTrack && (
              <Button
                variant="outline"
                onClick={handleRegenerateWithAi}
                disabled={
                  !videoUrl || isLoadingTrack || isSaving || isRegenerating
                }
              >
                {isRegenerating
                  ? t('pages.edit_subtitle.regenerating_with_ai', {
                      defaultValue: 'Regenerating...',
                    })
                  : t('pages.edit_subtitle.regenerate_with_ai', {
                      defaultValue: 'Regenerate with AI',
                    })}
              </Button>
            )}

            <Button
              onClick={handleSave}
              disabled={
                !dirty ||
                isLoadingTrack ||
                isSaving ||
                isRegenerating ||
                isWaitingForRegeneratedTrack
              }
            >
              {isSaving
                ? t('button.saving', { defaultValue: 'Saving...' })
                : t('pages.edit_subtitle.save_subtitles', {
                    defaultValue: 'Save subtitles',
                  })}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SubtitleEditorPage;
